import type { ColumnDef, RowSelectionState, Table } from '@tanstack/react-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';

import { useColumns } from './documents-table-columns';
import type { UploadedDocument } from './types';

type UseDocumentsTableProps = {
    readonly documents: readonly UploadedDocument[];
    readonly onDelete: (id: string, name: string) => void;
    readonly onDownload: (id: string) => void;
};

type UseDocumentsTableReturn = {
    readonly clearSelection: () => void;
    readonly selectedDocuments: readonly UploadedDocument[];
    readonly table: Table<UploadedDocument>;
};

export const useDocumentsTable = ({
    documents,
    onDelete,
    onDownload,
}: UseDocumentsTableProps): UseDocumentsTableReturn => {
    const {
        applicationState: { languageKey },
    } = useApplicationContext();
    const translate = useTranslate();
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const selectedDocumentNames = useMemo(
        () => Object.keys(rowSelection).filter((key) => rowSelection[key] === true),
        [rowSelection],
    );

    const selectedDocuments = useMemo(
        () => documents.filter((doc) => selectedDocumentNames.includes(doc.name)),
        [documents, selectedDocumentNames],
    );

    const columns = useColumns({
        locale: languageKey,
        onDelete,
        onDownload,
        translate,
    });

    const table = useReactTable({
        columns: columns as ColumnDef<UploadedDocument>[],
        data: documents as UploadedDocument[],
        enableRowSelection: true,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.name,
        onRowSelectionChange: setRowSelection,
        state: { rowSelection },
    });

    const clearSelection = (): void => {
        setRowSelection({});
    };

    return {
        clearSelection,
        selectedDocuments,
        table,
    };
};
